/**
 * RIS_BASIC_01/modals/ItemSelectModal.js 와 동일 (설비·물질 선택, 경로만 Step01용)
 */
import React, { useState, useEffect } from 'react';
import { FACILITY_OTHER_CATEGORY_VALUE } from '../hooks/useItemSelection';
import {
  MATERIAL_OTHER_CATEGORY_VALUE,
  MANUAL_MATERIAL_PRODUCT_ID,
} from '../SafetyInfo_Schema';
import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Dialog, Button } from '../../../../../../Components/MUIs';

/**
 * ItemSelectModal 컴포넌트
 * 설비 또는 물질을 선택하기 위한 통합 모달
 *
 * 3패널 레이아웃:
 * - 왼쪽: 카테고리/제품 선택 (체크박스)
 * - 가운데: 선택된 카테고리로 필터링된 항목 목록 (체크박스)
 * - 오른쪽: 선택된 항목 (제거 버튼이 있는 칩)
 *
 * modalConfig 객체를 기반으로 동적으로 구성됨
 *
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Function} props.onConfirm - 확인 핸들러 (선택 저장)
 * @param {Function} [props.onSaveManualFacility] - 수기 설비 신규/수정 (name, { chaebun }?) 성공 시 true
 * @param {Function} [props.onDeleteManualFacility] - 수기 설비 삭제(chaebun) 성공 시 true
 * @param {Function} [props.onSaveManualMaterial] - 수기 물질 신규/수정 (name, { hbdId }?) 성공 시 true
 * @param {Function} [props.onDeleteManualMaterial] - 수기 물질 삭제(hbdId) 성공 시 true
 * @param {Object} props.modalConfig - useItemSelection.getModalConfig()에서 가져온 모달 설정 객체
 * @param {string} props.modalType - 모달 타입: 'facility' | 'material'
 * @returns {JSX.Element} ItemSelectModal 컴포넌트
 */
const ItemSelectModal = ({
  open,
  onClose,
  onConfirm,
  onSaveManualFacility,
  onDeleteManualFacility,
  onSaveManualMaterial,
  onDeleteManualMaterial,
  modalConfig,
  modalType,
}) => {
  const [manualFacilityName, setManualFacilityName] = useState('');
  const [manualSavePending, setManualSavePending] = useState(false);
  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [manualEditChaebun, setManualEditChaebun] = useState('');
  const [manualEditName, setManualEditName] = useState('');
  const [manualEditPending, setManualEditPending] = useState(false);
  const [manualDeleteOpen, setManualDeleteOpen] = useState(false);
  const [manualDeleteChaebun, setManualDeleteChaebun] = useState('');
  const [manualDeleteLabel, setManualDeleteLabel] = useState('');
  const [manualDeletePending, setManualDeletePending] = useState(false);

  useEffect(() => {
    if (!open) {
      setManualEditOpen(false);
      setManualDeleteOpen(false);
      setManualEditChaebun('');
      setManualEditName('');
      setManualDeleteChaebun('');
      setManualDeleteLabel('');
    }
  }, [open]);

  if (!modalConfig) return null;

  const isOtherFacilityCategorySelected =
    modalType === 'facility' &&
    modalConfig.selectedCategories.some(
      (c) => c.value === FACILITY_OTHER_CATEGORY_VALUE
    );
  const isOtherMaterialCategorySelected =
    modalType === 'material' &&
    modalConfig.selectedCategories.some(
      (c) => String(c.value) === String(MANUAL_MATERIAL_PRODUCT_ID)
    );

  /** 기타(수기) 분류에서 온 설비 행 — TB_BO_HAZ_BASIC_04 등 직접 등록 */
  const isManualFacilityRow = (item) =>
    modalType === 'facility' &&
    item?.categoryCd === FACILITY_OTHER_CATEGORY_VALUE;
  const isManualMaterialRow = (item) =>
    modalType === 'material' &&
    (item?.categoryCd === MATERIAL_OTHER_CATEGORY_VALUE ||
      item?.isManual === true);

  const getManualRowId = (item) => {
    const v =
      modalType === 'material'
        ? (item?.hbdId ?? item?.materialId ?? '')
        : (item?.chaebun ?? item?.facilityCd ?? '');
    const s = String(v).trim();
    return s && s !== '0' ? s : '';
  };

  const canManageManualRow = (item) =>
    (isManualFacilityRow(item) || isManualMaterialRow(item)) &&
    !!getManualRowId(item);

  const onlyOtherFacilityCategorySelected =
    modalType === 'facility' &&
    modalConfig.selectedCategories?.length === 1 &&
    modalConfig.selectedCategories[0]?.value === FACILITY_OTHER_CATEGORY_VALUE;
  const onlyOtherMaterialCategorySelected =
    modalType === 'material' &&
    modalConfig.selectedCategories?.length === 1 &&
    String(modalConfig.selectedCategories[0]?.value) ===
      String(MANUAL_MATERIAL_PRODUCT_ID);

  /**
   * Get item display name (handles field name variations)
   * 실제 데이터 구조에 맞춰 machineName, substanceName을 직접 사용
   */
  const getItemDisplayName = (item) => {
    // 설비: machineName 사용
    if (modalType === 'facility') {
      return item.machineName || item.machineNm || '이름 없음';
    }
    // 물질: substanceName 사용
    if (modalType === 'material') {
      return item.substanceName || item.MATERIAL_NM || '이름 없음';
    }
    return '이름 없음';
  };

  /**
   * Get unique key for item (handles field name variations)
   * 설비: chaebun, 물질: hbdId를 고유 key로 사용
   * 정규화된 아이템의 구조를 고려하여 고유 키 생성
   */
  const getItemKey = (item, index) => {
    // 설비의 경우 chaebun을 고유 key로 사용
    if (modalType === 'facility') {
      const uniqueId = item.chaebun || item.facilityCd || `facility-${index}`;
      // 정규화된 아이템에는 categoryCd가 설정되어 있음
      const categoryId =
        item.categoryCd || item[modalConfig.itemGroupKey] || `cat-${index}`;
      // 고유 ID가 있으면 그것을 우선 사용 (chaebun이 고유하므로)
      return uniqueId || `${categoryId}-${index}`;
    }
    // 물질의 경우 hbdId를 고유 key로 사용
    if (modalType === 'material') {
      const uniqueId =
        item.hbdId ||
        item.materialId ||
        item.MATERIAL_CD ||
        `material-${index}`;
      // 정규화된 아이템에는 productId가 설정되어 있음
      const productId =
        item.productId ||
        item.hbmId ||
        item[modalConfig.itemGroupKey] ||
        `prod-${index}`;
      // 고유 ID가 있으면 그것을 우선 사용 (hbdId가 고유하므로)
      return uniqueId || `${productId}-${index}`;
    }
    // 기본값
    return `item-${index}`;
  };

  /**
   * Filter categories by search text
   */
  const filteredCategories = modalConfig.categories.filter(
    (cat) =>
      !modalConfig.categorySearchText ||
      cat.label
        ?.toLowerCase()
        .includes(modalConfig.categorySearchText.toLowerCase())
  );

  /**
   * Filter items by search text
   */
  const filteredItems = modalConfig.items.filter(
    (item) =>
      !modalConfig.searchText ||
      getItemDisplayName(item)
        ?.toLowerCase()
        .includes(modalConfig.searchText.toLowerCase())
  );

  const OpenManualEditDialog = (item) => {
    const id = getManualRowId(item);
    const saveHandler =
      modalType === 'material' ? onSaveManualMaterial : onSaveManualFacility;
    if (!id || !saveHandler) return;
    setManualEditChaebun(id);
    setManualEditName(getItemDisplayName(item));
    setManualEditOpen(true);
  };

  const SubmitManualEdit = async () => {
    const name = manualEditName.trim();
    const saveHandler =
      modalType === 'material' ? onSaveManualMaterial : onSaveManualFacility;
    if (!name || !saveHandler) return;
    setManualEditPending(true);
    try {
      const ok = await saveHandler(
        name,
        modalType === 'material'
          ? { hbdId: manualEditChaebun }
          : { chaebun: manualEditChaebun }
      );
      if (ok) {
        modalConfig.patchSelectedItemByItemKey?.(manualEditChaebun, {
          ...(modalType === 'material'
            ? { substanceName: name, materialName: name, materialNm: name }
            : { machineName: name }),
        });
        setManualEditOpen(false);
      }
    } finally {
      setManualEditPending(false);
    }
  };

  const SubmitManualDelete = async () => {
    const deleteHandler =
      modalType === 'material'
        ? onDeleteManualMaterial
        : onDeleteManualFacility;
    if (!manualDeleteChaebun || !deleteHandler) return;
    setManualDeletePending(true);
    try {
      const ok = await deleteHandler(manualDeleteChaebun);
      if (ok) {
        modalConfig.removeSelectedItemByItemKey?.(manualDeleteChaebun);
        setManualDeleteOpen(false);
      }
    } finally {
      setManualDeletePending(false);
    }
  };

  return (
    <>
      <Dialog
        title={modalConfig.title}
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: 'min(1200px, 96vw)' },
            height: '72vh',
            maxHeight: '820px',
          },
        }}
        actions={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              name="취소"
              onClick={onClose}
              sx={{
                color: '#fff',
                backgroundColor: '#D32F2F',
                '&:hover': {
                  backgroundColor: '#B71C1C',
                },
              }}
              size="small"
            />
            <Button
              name="선택하기"
              onClick={() => onConfirm(modalConfig.selectedItems)}
              sx={{
                color: '#fff',
                backgroundColor: '#2CA900',
                '&:hover': {
                  backgroundColor: '#249100',
                },
              }}
              size="small"
            />
          </Box>
        }
        useSaveButton={false}
        useCloseButton={false}
        content={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: '100%',
              minHeight: 0,
            }}
          >
            {modalType === 'facility' && (
              <Alert
                severity="info"
                sx={{
                  py: 0.75,
                  mt: 1,
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ lineHeight: 1.6 }}
                >
                  등록된 목록에 없는 설비를 추가하려면, 왼쪽{' '}
                  <strong>설비 분류</strong>에서 <strong>수기 등록 설비</strong>
                  를 선택한 뒤 가운데 영역에 설비명을 입력하고{' '}
                  <strong>설비 등록</strong>을 누르세요.
                </Typography>
              </Alert>
            )}
            {modalType === 'material' && (
              <Alert
                severity="info"
                sx={{
                  py: 0.75,
                  mt: 1,
                  '& .MuiAlert-message': { width: '100%' },
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ lineHeight: 1.6 }}
                >
                  등록된 목록에 없는 물질을 추가하려면, 왼쪽{' '}
                  <strong>제품명</strong>
                  에서 <strong>수기 등록 물질</strong>을 선택한 뒤 가운데 영역에
                  물질명을 입력하고 <strong>물질 등록</strong>을 누르세요.
                </Typography>
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flex: 1,
                minHeight: 0,
                minWidth: 0,
              }}
            >
              {/* Left Panel: Category Selection */}
              {modalConfig.categories && modalConfig.categories.length > 0 && (
                <Paper
                  sx={{
                    flex: '0 0 240px',
                    minWidth: 200,
                    maxWidth: 280,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}
                  >
                    {modalConfig.categoryTitle}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder={modalConfig.categorySearchPlaceholder}
                    value={modalConfig.categorySearchText}
                    onChange={(e) =>
                      modalConfig.setCategorySearchText(e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <List
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#fff',
                    }}
                  >
                    {filteredCategories.map((cat) => (
                      <ListItem key={cat.value} disablePadding>
                        <ListItemButton
                          onClick={() => modalConfig.toggleCategory(cat)}
                          sx={{
                            backgroundColor:
                              modalConfig.selectedCategories.some(
                                (c) => c.value === cat.value
                              )
                                ? '#e8f5e9'
                                : 'transparent',
                            border: modalConfig.selectedCategories.some(
                              (c) => c.value === cat.value
                            )
                              ? '1px solid #4caf50'
                              : 'none',
                            borderRadius: 3,
                            mx: 0.5,
                            my: 0.25,
                            '&:hover': {
                              backgroundColor:
                                modalConfig.selectedCategories.some(
                                  (c) => c.value === cat.value
                                )
                                  ? '#c8e6c9'
                                  : '#f5f5f5',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={modalConfig.selectedCategories.some(
                                (c) => c.value === cat.value
                              )}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${cat.label} (${modalConfig.getCategoryCount(cat)})`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Middle Panel: Item List */}
              <Paper
                sx={{
                  flex: '1 1 280px',
                  minWidth: { xs: 240, sm: 300 },
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#f8f9fa',
                  minHeight: 0,
                }}
              >
                <TextField
                  size="small"
                  placeholder={modalConfig.searchPlaceholder}
                  value={modalConfig.searchText}
                  onChange={(e) => modalConfig.setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1.5 }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    전체 ({filteredItems.length})
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {isOtherFacilityCategorySelected && (
                      <>
                        <TextField
                          size="small"
                          placeholder="설비명을 입력하세요"
                          value={manualFacilityName}
                          onChange={(e) =>
                            setManualFacilityName(e.target.value)
                          }
                          sx={{ minWidth: 160 }}
                        />
                        <Button
                          size="small"
                          name="설비 등록"
                          disabled={manualSavePending}
                          onClick={async () => {
                            const name = manualFacilityName.trim();
                            if (!name || !onSaveManualFacility) return;
                            setManualSavePending(true);
                            try {
                              const ok = await onSaveManualFacility(name);
                              if (ok) setManualFacilityName('');
                            } finally {
                              setManualSavePending(false);
                            }
                          }}
                          sx={{
                            color: '#fff',
                            backgroundColor: '#2CA900',
                            '&:hover': {
                              backgroundColor: '#249100',
                            },
                            fontSize: '0.75rem',
                            px: 1.5,
                            py: 0.5,
                          }}
                        />
                      </>
                    )}
                    {isOtherMaterialCategorySelected && (
                      <>
                        <TextField
                          size="small"
                          placeholder="물질명을 입력하세요"
                          value={manualFacilityName}
                          onChange={(e) =>
                            setManualFacilityName(e.target.value)
                          }
                          sx={{ minWidth: 160 }}
                        />
                        <Button
                          size="small"
                          name="물질 등록"
                          disabled={manualSavePending}
                          onClick={async () => {
                            const name = manualFacilityName.trim();
                            if (!name || !onSaveManualMaterial) return;
                            setManualSavePending(true);
                            try {
                              const ok = await onSaveManualMaterial(name);
                              if (ok) setManualFacilityName('');
                            } finally {
                              setManualSavePending(false);
                            }
                          }}
                          sx={{
                            color: '#fff',
                            backgroundColor: '#2CA900',
                            '&:hover': {
                              backgroundColor: '#249100',
                            },
                            fontSize: '0.75rem',
                            px: 1.5,
                            py: 0.5,
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
                {onlyOtherFacilityCategorySelected && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      py: 0.5,
                      px: 1,
                      mb: 1,
                      fontSize: '0.8rem',
                      '& .MuiAlert-message': { py: 0 },
                    }}
                  >
                    아래 목록은 <strong>직접 입력·등록한 설비</strong>입니다.
                  </Alert>
                )}
                {onlyOtherMaterialCategorySelected && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{
                      py: 0.5,
                      px: 1,
                      mb: 1,
                      fontSize: '0.8rem',
                      '& .MuiAlert-message': { py: 0 },
                    }}
                  >
                    아래 목록은 <strong>직접 입력·등록한 물질</strong>입니다.
                  </Alert>
                )}
                <List
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#fff',
                  }}
                >
                  {modalConfig.selectedCategories?.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary={
                          modalConfig.noSelectionMessage ||
                          '분류를 먼저 선택하세요'
                        }
                        sx={{ color: '#999', textAlign: 'center' }}
                      />
                    </ListItem>
                  ) : (
                    filteredItems.map((item, index) => {
                      const showManage =
                        canManageManualRow(item) &&
                        (modalType === 'material'
                          ? onSaveManualMaterial || onDeleteManualMaterial
                          : onSaveManualFacility || onDeleteManualFacility);
                      return (
                        <ListItem
                          key={getItemKey(item, index)}
                          disablePadding
                          secondaryAction={
                            showManage ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexShrink: 0,
                                  pl: 0.5,
                                  pr: 0.25,
                                }}
                              >
                                {(modalType === 'material'
                                  ? onSaveManualMaterial
                                  : onSaveManualFacility) && (
                                  <Tooltip
                                    title={
                                      modalType === 'material'
                                        ? '물질명 수정'
                                        : '설비명 수정'
                                    }
                                  >
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={() => OpenManualEditDialog(item)}
                                      aria-label={
                                        modalType === 'material'
                                          ? '수기 물질 수정'
                                          : '수기 설비 수정'
                                      }
                                    >
                                      <EditOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {(modalType === 'material'
                                  ? onDeleteManualMaterial
                                  : onDeleteManualFacility) && (
                                  <Tooltip title="삭제">
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={() => {
                                        setManualDeleteChaebun(
                                          getManualRowId(item)
                                        );
                                        setManualDeleteLabel(
                                          getItemDisplayName(item)
                                        );
                                        setManualDeleteOpen(true);
                                      }}
                                      aria-label={
                                        modalType === 'material'
                                          ? '수기 물질 삭제'
                                          : '수기 설비 삭제'
                                      }
                                    >
                                      <DeleteOutlineIcon
                                        fontSize="small"
                                        color="error"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            ) : null
                          }
                        >
                          <ListItemButton
                            onClick={() => modalConfig.toggleItem(item)}
                            sx={{
                              backgroundColor: modalConfig.isItemSelected(item)
                                ? '#e8f5e9'
                                : 'transparent',
                              border: modalConfig.isItemSelected(item)
                                ? '1px solid #4caf50'
                                : 'none',
                              borderRadius: 3,
                              mx: 0.5,
                              my: 0.25,
                              pr: showManage ? 15 : 0.5,
                              alignItems: 'flex-start',
                              '&:hover': {
                                backgroundColor: modalConfig.isItemSelected(
                                  item
                                )
                                  ? '#c8e6c9'
                                  : '#f5f5f5',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                              <Checkbox
                                edge="start"
                                checked={modalConfig.isItemSelected(item)}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              sx={{
                                minWidth: 0,
                                pr: 0,
                                '& .MuiListItemText-primary': {
                                  whiteSpace: 'normal',
                                },
                                '& .MuiListItemText-secondary': {
                                  whiteSpace: 'normal',
                                  wordBreak: 'keep-all',
                                },
                              }}
                              primary={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <span>{getItemDisplayName(item)}</span>
                                  {(isManualFacilityRow(item) ||
                                    isManualMaterialRow(item)) && (
                                    <Chip
                                      label="수기등록"
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: '#fff3e0',
                                        color: '#e65100',
                                        border: '1px solid #ffb74d',
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                isManualFacilityRow(item)
                                  ? '직접 입력·등록된 설비입니다. (수정·삭제 가능)'
                                  : isManualMaterialRow(item)
                                    ? '직접 입력·등록된 물질입니다. (수정·삭제 가능)'
                                    : item[modalConfig.itemSecondary]
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Paper>

              {/* Right Panel: Selected Items */}
              <Paper
                sx={{
                  flex: '0.95 1 260px',
                  minWidth: 220,
                  maxWidth: 340,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#f8f9fa',
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: modalConfig.color }}
                  >
                    {modalConfig.selectedItems.length}개 선택됨
                  </Typography>
                  <Button
                    size="small"
                    name="전체 삭제"
                    onClick={modalConfig.clearAll}
                    sx={{
                      color: '#f44336',
                      fontSize: '0.75rem',
                      backgroundColor: '#fff',
                      border: '1px solid #f44336',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      '&:hover': {
                        backgroundColor: '#ffebee',
                        borderColor: '#d32f2f',
                      },
                    }}
                    variant="outlined"
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    backgroundColor: '#fff',
                    p: 1,
                  }}
                >
                  {modalConfig.selectedItems.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#999',
                      }}
                    >
                      <Typography variant="body2">
                        {modalConfig.emptyMessage}
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {modalConfig.selectedItems.map((item) => (
                        <Chip
                          key={
                            item[modalConfig.itemKey] ||
                            item.machineId ||
                            item.chaebun ||
                            item.id
                          }
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                maxWidth: '100%',
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {getItemDisplayName(item)}
                              </span>
                              {(isManualFacilityRow(item) ||
                                isManualMaterialRow(item)) && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    px: 0.5,
                                    py: 0.1,
                                    borderRadius: 0.5,
                                    bgcolor: '#fff3e0',
                                    color: '#e65100',
                                    border: '1px solid #ffb74d',
                                    flexShrink: 0,
                                  }}
                                >
                                  수기
                                </Box>
                              )}
                            </Box>
                          }
                          onDelete={() => modalConfig.toggleItem(item)}
                          sx={{
                            justifyContent: 'space-between',
                            backgroundColor: modalConfig.chipBgColor,
                            color: modalConfig.color,
                            height: 'auto',
                            py: 0.5,
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                            },
                            '& .MuiChip-deleteIcon': {
                              color: modalConfig.color,
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        }
      />
      <MuiDialog
        open={manualEditOpen}
        onClose={() => !manualEditPending && setManualEditOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {modalType === 'material' ? '물질명 수정' : '설비명 수정'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={modalType === 'material' ? '물질명' : '설비명'}
            value={manualEditName}
            onChange={(e) => setManualEditName(e.target.value)}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            name="취소"
            onClick={() => !manualEditPending && setManualEditOpen(false)}
            disabled={manualEditPending}
            size="small"
          />
          <Button
            name="저장"
            onClick={SubmitManualEdit}
            disabled={manualEditPending || !manualEditName.trim()}
            sx={{
              color: '#fff',
              backgroundColor: '#2CA900',
              '&:hover': { backgroundColor: '#249100' },
            }}
            size="small"
          />
        </DialogActions>
      </MuiDialog>
      <MuiDialog
        open={manualDeleteOpen}
        onClose={() => !manualDeletePending && setManualDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>삭제</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            <strong>{manualDeleteLabel}</strong>{' '}
            {modalType === 'material' ? '물질을' : '설비를'} 삭제하시겠습니까?
            이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            name="취소"
            onClick={() => !manualDeletePending && setManualDeleteOpen(false)}
            disabled={manualDeletePending}
            size="small"
          />
          <Button
            name="삭제"
            onClick={SubmitManualDelete}
            disabled={manualDeletePending}
            sx={{
              color: '#fff',
              backgroundColor: '#D32F2F',
              '&:hover': { backgroundColor: '#B71C1C' },
            }}
            size="small"
          />
        </DialogActions>
      </MuiDialog>
    </>
  );
};

export default ItemSelectModal;
